import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { ScreenLayout, Header, FormHeader, FormHeaderAction } from '../components';
import { CalendarForm } from '../components/calendar';
import { CalendarSharingModal } from '../components/CalendarSharingModal';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface CalendarEditScreenProps {
  calendarId?: string;
}

export const CalendarEditScreen: React.FC<CalendarEditScreenProps> = ({
  calendarId: propCalendarId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [calendar, setCalendar] = useState<any>(null);
  const [sharingModalVisible, setSharingModalVisible] = useState(false);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const calendarId = propCalendarId || (Platform.OS === 'web' ? new URLSearchParams(window.location.search).get('calendarId') : null);

  const loadCalendar = useCallback(async () => {
    if (!calendarId || !currentBusiness?._id) {
      console.log('📅 [CalendarEditScreen] Cannot load calendar - missing calendarId or businessId', {
        calendarId,
        businessId: currentBusiness?._id
      });
      return;
    }

    try {
      setLoadingCalendar(true);
      console.log('📅 [CalendarEditScreen] Loading calendar:', calendarId);
      const response = await apiService.getCalendarById(calendarId, currentBusiness._id);

      if (response.statusCode === 1000 && response.data) {
        // Convert date strings to Date objects for availability fields
        const calendarData = { ...response.data };
        if (calendarData.availability) {
          if (calendarData.availability.startDate && typeof calendarData.availability.startDate === 'string') {
            calendarData.availability.startDate = new Date(calendarData.availability.startDate);
          }
          if (calendarData.availability.endDate && typeof calendarData.availability.endDate === 'string') {
            calendarData.availability.endDate = new Date(calendarData.availability.endDate);
          }
        }
        setCalendar(calendarData);
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.edit.loadFailed');
        showNotification('error', 'Error', errorMessage);
        navigateTo('calendars');
      }
    } catch (error: any) {
      console.error('Error loading calendar:', error);
      showNotification('error', 'Error', error.message || t('calendar.edit.loadFailed'));
      navigateTo('calendars');
    } finally {
      setLoadingCalendar(false);
    }
  }, [calendarId, currentBusiness?._id, showNotification, navigateTo]);

  useEffect(() => {
    if (calendarId && currentBusiness?._id) {
      loadCalendar();
    } else {
      // If we don't have calendarId or businessId yet, stop loading
      if (calendarId && !currentBusiness?._id) {
        console.log('📅 [CalendarEditScreen] Waiting for business to load...');
      } else if (!calendarId) {
        console.log('📅 [CalendarEditScreen] Calendar ID not provided');
        setLoadingCalendar(false);
        showNotification('error', 'Error', t('calendar.edit.calendarIdRequired'));
        navigateTo('calendars');
      }
    }
  }, [calendarId, currentBusiness?._id, loadCalendar]);

  const handleSubmit = async (formData: any) => {
    if (!calendarId || !currentBusiness?._id) {
      showNotification('error', 'Error', t('calendar.edit.businessRequired'));
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.updateCalendar(calendarId, formData);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.edit.updateSuccess'));
        navigateTo('calendars');
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.edit.updateFailed');
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error updating calendar:', error);
      showNotification('error', 'Error', error.message || t('calendar.edit.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      // Use navigateTo instead of window.history.back() to ensure proper navigation state update
      navigateTo('calendars');
    } else {
      navigateTo('calendars');
    }
  };

  if (loadingCalendar) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent="empty"
      >
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('calendar.edit.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (!calendar) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent="empty"
      >
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>{t('calendar.edit.notFound')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title={t('calendar.edit.title')}
          subtitle={t('calendar.edit.subtitle')}
          onBack={handleCancel}
          actions={[
            ...(calendar.businessId
              ? [
                  {
                    icon: 'people-outline',
                    onPress: () => setSharingModalVisible(true),
                    label: 'Sharing',
                    accessibilityLabel: 'Sharing',
                  } as FormHeaderAction,
                ]
              : []),
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading,
              disabled: loading,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.formContainer}>
            <CalendarForm
              initialData={calendar}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              submitLabel={t('calendar.edit.submitLabel')}
              loading={loading}
              businessId={currentBusiness?._id || ''}
              userId={user?.id || ''}
              hideActions={true}
              submitRef={formSubmitRef}
              calendarId={calendarId || undefined}
              onNotify={showNotification}
            />
          </View>
        </ScrollView>
      </View>

      {!!calendar.businessId && (
        <CalendarSharingModal
          visible={sharingModalVisible}
          onClose={() => setSharingModalVisible(false)}
          calendarId={calendarId!}
          calendarName={calendar.name}
          currentSharedWith={calendar.sharedWith || []}
          onUpdated={(sharedWith) => setCalendar((prev: any) => ({ ...prev, sharedWith }))}
          onNotify={showNotification}
        />
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  formContainer: {
    padding: spacing.md,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.md,
  },
  errorText: {
    ...typography.body,
    color: colors.error,
    marginTop: spacing.md,
    textAlign: 'center',
  },
});

